import { useMemo, useState, useCallback } from "react";
import { CopTreeNode, ManageCopTreeNodesData } from "./useCopTreeNodes";
import { ColumnData } from "./CopTreeFinder";

export const useCopTreeFinder = (treeData: ManageCopTreeNodesData) => {
  const [selectedPathIds, setSelectedPathIds] = useState<number[]>([]);
  const filterText = (treeData.filter.filter || "").trim().toLowerCase();

  const getNodePathKey = useCallback((node: CopTreeNode) => {
    return node.path ? `${node.path}.${node._id}` : `${node._id}`;
  }, []);

  // 1. Build hierarchy index and compute ancestor-inclusive search visibility
  const { nodesById, childrenByParentKey, rootNodes, matchedNodeIds } = useMemo(() => {
    const allNodes = treeData.data;
    const nodesById = new Map<number, CopTreeNode>();
    allNodes.forEach(node => nodesById.set(node._id, node));

    // Calculate search matching & ancestor visibility
    const matchedNodeIds = new Set<number>();
    let visibleNodeIds: Set<number> | null = null;

    if (filterText) {
      visibleNodeIds = new Set<number>();

      allNodes.forEach(node => {
        const matches =
          node.title?.toLowerCase().includes(filterText) ||
          node.type?.toLowerCase().includes(filterText) ||
          String(node._id).includes(filterText);

        if (matches) {
          matchedNodeIds.add(node._id);
          visibleNodeIds!.add(node._id);

          // Keep all ancestor nodes visible so the path to this match is browsable
          if (node.path) {
            const ancestorIds = node.path.split('.').filter(Boolean);
            ancestorIds.forEach(idStr => {
              const ancestorId = Number(idStr);
              if (!isNaN(ancestorId)) {
                visibleNodeIds!.add(ancestorId);
              }
            });
          }
        }
      });
    }

    const childrenByParentKey = new Map<string, CopTreeNode[]>();
    const rootNodes: CopTreeNode[] = [];

    allNodes.forEach(node => {
      // If search filter is active, skip nodes that are not matches or ancestors of matches
      if (visibleNodeIds && !visibleNodeIds.has(node._id)) {
        return;
      }

      if (!node.path || node.path.trim() === "") {
        rootNodes.push(node);
      } else {
        const parentList = childrenByParentKey.get(node.path) || [];
        parentList.push(node);
        childrenByParentKey.set(node.path, parentList);
      }
    });

    // Sort siblings by sort_order
    rootNodes.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    childrenByParentKey.forEach(list => {
      list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    });

    return { nodesById, childrenByParentKey, rootNodes, matchedNodeIds };
  }, [treeData.data, filterText]);

  const hasChildren = useCallback(
    (node: CopTreeNode) => {
      const key = getNodePathKey(node);
      const children = childrenByParentKey.get(key);
      return Boolean(children && children.length > 0);
    },
    [childrenByParentKey, getNodePathKey]
  );

  // 2. Build Miller columns based on active selection path
  const columns: ColumnData[] = useMemo(() => {
    const cols: ColumnData[] = [{ parent: undefined, items: rootNodes }];

    for (let i = 0; i < selectedPathIds.length; i++) {
      const parentId = selectedPathIds[i];
      const parentNode = nodesById.get(parentId);
      if (!parentNode) break;

      const key = getNodePathKey(parentNode);
      const children = childrenByParentKey.get(key) || [];

      cols.push({ parent: parentNode, items: children });
    }

    return cols;
  }, [rootNodes, selectedPathIds, nodesById, childrenByParentKey, getNodePathKey]);

  const selectNode = useCallback((columnIndex: number, node: CopTreeNode) => {
    setSelectedPathIds(prev => {
      const next = prev.slice(0, columnIndex);
      next[columnIndex] = node._id;
      return next;
    });
  }, []);

  // 3. Robust child / sibling creation setting path and sort order immediately
  const startCreateChild = useCallback(
    (parentNode?: CopTreeNode) => {
      const parentPath = parentNode ? getNodePathKey(parentNode) : null;
      const siblings = parentPath
        ? childrenByParentKey.get(parentPath) || []
        : rootNodes;

      const nextOrder = Math.max(...siblings.map(s => s.sort_order ?? 0), 0) + 1;

      const initialValues: Partial<CopTreeNode> = {
        title: "",
        type: parentNode ? parentNode.type : "",
        path: parentPath,
        sort_order: nextOrder,
        is_hidden: false,
        color: null,
        image_url: null,
        description: null,
      };

      // Pass initialValues to startEditing to seed state before opening the dialog
      treeData.newNode.startEditing(initialValues as any);
      treeData.newNode.update(initialValues);
    },
    [childrenByParentKey, getNodePathKey, rootNodes, treeData.newNode]
  );

  const selectPathToNode = useCallback((node: CopTreeNode | null) => {
    if (!node) {
      // Moved to root: reset selection so Column 0 (Root) is active
      setSelectedPathIds([]);
      return;
    }

    const ancestorIds = node.path
      ? node.path
          .split('.')
          .filter(Boolean)
          .map(Number)
          .filter(id => !isNaN(id))
      : [];

    // [rootAncestorId, ..., parentAncestorId, node._id]
    setSelectedPathIds([...ancestorIds, node._id]);
  }, []);

  return {
    ...treeData,
    rootNodes,
    nodesById,
    columns,
    selectedPathIds,
    matchedNodeIds,
    filterText,
    hasChildren,
    selectNode,
    startCreateChild,
    setSelectedPathIds,
    selectPathToNode,
  };
};